import { useRef, useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { Segment } from '@/types'

interface TranscriptViewerProps {
  segments: Segment[]
  currentTime: number
  onSegmentClick?: (time: number) => void
  className?: string
}

export function TranscriptViewer({
  segments,
  currentTime,
  onSegmentClick,
  className,
}: TranscriptViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const activeSegmentRef = useRef<HTMLDivElement>(null)

  // Find current segment index
  const currentSegmentIndex = useMemo(() => {
    for (let i = segments.length - 1; i >= 0; i--) {
      if (currentTime >= segments[i].start) {
        return i
      }
    }
    return -1
  }, [segments, currentTime])

  // Auto-scroll to active segment
  useEffect(() => {
    if (activeSegmentRef.current && containerRef.current) {
      const container = containerRef.current
      const element = activeSegmentRef.current
      const containerRect = container.getBoundingClientRect()
      const elementRect = element.getBoundingClientRect()

      // Only scroll if element is outside visible area
      if (
        elementRect.top < containerRect.top ||
        elementRect.bottom > containerRect.bottom
      ) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [currentSegmentIndex])

  const formatTimestamp = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  // Group consecutive segments by speaker
  const groupedSegments = useMemo(() => {
    const groups: { speaker: string; segments: (Segment & { index: number })[] }[] = []
    let currentGroup: { speaker: string; segments: (Segment & { index: number })[] } | null = null

    segments.forEach((segment, index) => {
      const speaker = segment.speaker || 'Desconhecido'
      if (!currentGroup || currentGroup.speaker !== speaker) {
        currentGroup = { speaker, segments: [] }
        groups.push(currentGroup)
      }
      currentGroup.segments.push({ ...segment, index })
    })

    return groups
  }, [segments])

  if (segments.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-64 text-muted-foreground', className)}>
        Nenhuma transcrição disponível
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn('overflow-y-auto space-y-4', className)}
    >
      {groupedSegments.map((group, groupIndex) => (
        <div key={groupIndex} className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground sticky top-0 bg-background py-1">
            {group.speaker}
          </div>
          <div className="space-y-2 pl-2 border-l-2 border-muted">
            {group.segments.map((segment) => {
              const isActive = segment.index === currentSegmentIndex
              const isPast = segment.index < currentSegmentIndex

              return (
                <div
                  key={segment.index}
                  ref={isActive ? activeSegmentRef : undefined}
                  className={cn(
                    'p-2 rounded-md cursor-pointer transition-all',
                    isActive && 'bg-primary/10 border-l-2 border-primary -ml-[2px] pl-[10px]',
                    isPast && 'text-muted-foreground',
                    !isActive && !isPast && 'hover:bg-muted/50'
                  )}
                  onClick={() => onSegmentClick?.(segment.start)}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={cn(
                        'text-xs font-mono shrink-0 mt-0.5',
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      )}
                    >
                      {formatTimestamp(segment.start)}
                    </span>
                    <p className={cn('text-sm leading-relaxed', isActive && 'font-medium')}>
                      {segment.text}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
